import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "@/App.css";
import { Phone, MessageCircle, Mail, Wrench, Search, Users, Target, Camera, Star, CheckCircle, Zap, Settings, FileCheck, HeadphonesIcon, TrendingUp, Award, Heart, Download, Globe, MapPin } from "lucide-react";
import CookieBanner from "./CookieBanner";

// Assets
const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_ce22ad00-db82-461a-af39-4177f369e9d1/artifacts/wsj2zzd9_ETHIDA%202%20BLACK%20Mittel.png";
const PORTRAIT = "https://customer-assets.emergentagent.com/job_energieloesungen-de/artifacts/fay7ijjb_enes-Photoroom.png";
const ICON_GREEN = "https://customer-assets.emergentagent.com/job_ce22ad00-db82-461a-af39-4177f369e9d1/artifacts/xi1gt3jr_ETHIDA_2_Icon3_Green_White-removebg-preview.png";

// Contact Info
const CONTACT = {
  phone: "+49 176 41234 614",
  phoneLink: "tel:+4917641234614",
  whatsapp: "https://wa.me/4917641234614",
  email: "info@enthida.de",
  emailLink: "mailto:info@enthida.de",
  web: "www.enthida.de",
  region: "Raum Eggenfelden +100 km"
};

// Custom Icon Components
const CustomIcon = ({ children, className = "" }) =>
<div className={`service-icon ${className}`}>
    {children}
  </div>;


// Hero Section - Mobile First (QR-Code optimiert)
const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-grid-pattern" />
      <div className="hero-spotlight" />
      
      <div className="container mx-auto px-5 py-10 min-h-[90vh] flex items-center">
        <div className={`space-y-6 text-center ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <img
            src={LOGO_MAIN}
            alt="ENTHIDA - Smart Energy Systems"
            className="h-48 md:h-56 w-auto mx-auto"
            data-testid="hero-logo" />

          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            Elektromeister für{" "}
            <span className="gradient-text">PV-Abnahmen</span>,{" "}
            Inbetriebnahme und smarte Energielösungen
          </h1>
          
          {/* Verfügbarkeits-Badge */}
          <div className="flex flex-wrap justify-center gap-2 print:hidden">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#a3e635]/10 border border-[#a3e635]/30 rounded-full text-[#a3e635] text-sm font-medium" data-testid="availability-badge">
              <span className="w-2 h-2 bg-[#a3e635] rounded-full animate-pulse" />
              Aktuell verfügbar für neue Projekte
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#22d3ee]/10 border border-[#22d3ee]/30 rounded-full text-[#22d3ee] text-sm font-medium" data-testid="selberbauer-badge">
              Auch für Selberbauer & Eigeninstallation
            </span>
          </div>
          
          <p className="text-base md:text-lg text-slate-300 leading-relaxed max-w-lg mx-auto">
            Ich unterstütze bei der fachgerechten Fertigstellung von PV-Anlagen, 
            Prüfprotokollen, Kundeneinweisungen und intelligenten Energiekonzepten – 
            sauber, verständlich und praxisnah.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 pt-4 justify-center print:hidden">
            <a
              href={CONTACT.phoneLink}
              className="btn-primary text-base justify-center"
              data-testid="hero-call-button">

              <Phone size={18} />
              Jetzt anrufen
            </a>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-base justify-center"
              data-testid="hero-whatsapp-button">

              <MessageCircle size={18} />
              WhatsApp schreiben
            </a>
          </div>
          
          {/* Vertrauens-Trigger unter CTAs */}
          <p className="text-slate-400 text-xs text-center print:hidden" data-testid="trust-line">
            Kostenlose Erstberatung · Unverbindlich · Schnelle Antwort
          </p>
          
          {/* Zahlenleiste */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 pt-4 border-t border-white/10 mt-2" data-testid="stats-bar">
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black gradient-text">180+</div>
              <div className="text-slate-400 text-xs">Abnahmen</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black gradient-text">6+</div>
              <div className="text-slate-400 text-xs">Partnerfirmen</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-black gradient-text">100km+</div>
              <div className="text-slate-400 text-xs">Einsatzradius</div>
            </div>
          </div>
          
          {/* Print-only Kontaktdaten */}
          <div className="hidden print:block pt-6 text-center">
            <p className="text-slate-300 text-base">
              <span className="text-[#a3e635] font-semibold">Telefon:</span> {CONTACT.phone}
            </p>
            <p className="text-slate-300 text-base">
              <span className="text-[#a3e635] font-semibold">E-Mail:</span> {CONTACT.email}
            </p>
          </div>
        </div>
      </div>
    </section>);

};

// Print Button with Firefox hint modal
const FixedPrintButton = () => {
  const [showHint, setShowHint] = useState(false);

  return (
    <>
      <button
        onClick={() => setShowHint(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#a3e635] to-[#22d3ee] text-black font-semibold rounded-full shadow-lg hover:scale-105 hover:shadow-[0_0_25px_rgba(163,230,53,0.4)] transition-transform duration-300 print:hidden cursor-pointer"
        data-testid="print-button-fixed"
      >
        <Download size={18} />
        Als PDF drucken
      </button>

      {showHint && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm print:hidden" data-testid="print-hint-overlay">
          <div className="mx-4 max-w-sm w-full bg-[#0f1115] border border-white/10 rounded-2xl p-6 shadow-[0_0_40px_rgba(34,211,238,0.15)]" data-testid="print-hint-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#a3e635]/20 to-[#22d3ee]/20 rounded-xl">
                <Download className="w-5 h-5 text-[#a3e635]" />
              </div>
              <h3 className="text-lg font-bold text-white">Hinweis zum Druck</h3>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Die Druckvorschau und der PDF-Export funktionieren am besten mit dem <span className="text-[#a3e635] font-semibold">Firefox-Browser</span>. In anderen Browsern kann es zu Darstellungsunterschieden kommen.
            </p>
            <button
              onClick={() => { setShowHint(false); window.print(); }}
              className="w-full py-3 bg-gradient-to-r from-[#a3e635] to-[#22d3ee] text-black font-semibold rounded-full hover:scale-[1.02] transition-transform duration-200 cursor-pointer"
              data-testid="print-hint-accept-button"
            >
              Verstanden – weiter zum Druck
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// About Section - Mobile optimiert mit Kreis-Portrait
const AboutSection = () =>
<section className="section-gradient py-16 md:py-24" data-testid="about-section">
    <div className="container mx-auto px-5">
      <div className="flex flex-col items-center text-center">
        {/* Portrait im Kreis - größer */}
        <div className="relative mb-8">
          <div className="w-56 h-56 md:w-64 md:h-64 rounded-full bg-black p-1 ring-4 ring-[#22d3ee] shadow-[0_0_30px_rgba(34,211,238,0.4)] overflow-hidden">
            <img
            src={PORTRAIT}
            alt="Enes Dedic"
            className="w-full h-full object-cover object-[center_20%] scale-110 !rounded-full"
            data-testid="about-portrait" />

          </div>
        </div>
        
        <div className="flex items-center gap-3 mb-6">
          <img src={ICON_GREEN} alt="ENTHIDA Icon" className="w-10 h-10" />
          <h2 className="text-2xl md:text-3xl font-bold">
            Ihr Ansprechpartner für den{" "}
            <span className="gradient-text">letzten entscheidenden Schritt</span>
          </h2>
        </div>
        
        <div className="space-y-4 text-slate-400 text-base leading-relaxed max-w-2xl">
          <p>
            Ich bin Elektromeister mit Spezialisierung auf den Abschluss, 
            die Inbetriebnahme und die Problemlösung von Photovoltaikanlagen.
          </p>
          <p>
            Ich komme genau dann ins Spiel, wenn Projekte ins Stocken geraten, 
            Zeit fehlt oder der letzte Schritt sauber und professionell erledigt werden muss.
          </p>
          <p>
            Mein Fokus liegt nicht nur auf der Technik – sondern darauf, dass der 
            Auftrag wirklich abgeschlossen ist, der Kunde zufrieden ist und alles reibungslos läuft.
          </p>
        </div>
      </div>
    </div>
  </section>;


// Services Section - Mobile optimiert
const ServicesSection = () => {
  const services = [
  {
    id: "technical",
    icon: <Wrench strokeWidth={1.5} />,
    title: "Technischer Abschluss",
    items: [
    "Abnahme & Inbetriebnahme von PV-Anlagen",
    "Fertigmeldung & Netzbetreiber-Abwicklung",
    "Prüfung nach VDE / DGUV",
    "Integration von Speicher, Wallbox & Systemkomponenten"]

  },
  {
    id: "problem",
    icon: <Search strokeWidth={1.5} />,
    title: "Problemlösung & Fehlersuche",
    subtitle: "Das ist mein Joker",
    items: [
    "Analyse bei Kommunikationsproblemen (Wechselrichter, Speicher, Wallbox)",
    "Unterstützung bei 'funktioniert nicht'-Fällen",
    "Telefonische Voranalyse + gezielter Vor-Ort-Einsatz",
    "Schnelle Identifikation von typischen Fehlerquellen"],

    highlight: "Ich finde die Probleme, an denen andere hängen bleiben."
  },
  {
    id: "customer",
    icon: <Users strokeWidth={1.5} />,
    title: "Kundenabschluss & Einweisung",
    subtitle: "Das ist mein Gamechanger",
    items: [
    "Verständliche Erklärung der gesamten Anlage",
    "Klare Übergabe an den Kunden",
    "Strukturierter Abschluss des Projekts",
    "Unterstützung beim letzten Schritt: Vertrauen schaffen"],

    highlight: "Der Kunde weiß am Ende: 'Die Anlage läuft – und ich habe alles verstanden.'"
  }];


  return (
    <section className="py-20 md:py-28" data-testid="services-section">
      <div className="container mx-auto px-5 max-w-3xl">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Zap className="w-6 h-6 text-[#a3e635]" />
            <h2 className="text-2xl md:text-4xl font-bold">DIENSTLEISTUNGEN</h2>
          </div>
          <p className="text-slate-400 text-sm">Klar strukturiert für Ihren Erfolg</p>
        </div>
        
        <div className="space-y-8">
          {services.map((service, index) =>
          <div
            key={service.id}
            className="card p-6 md:p-8"
            data-testid={`service-card-${service.id}`}>

              <div className="flex items-start gap-4 mb-4">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#a3e635]/15 to-[#22d3ee]/15 rounded-xl">
                  <div className="text-[#a3e635]">{service.icon}</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1">{service.title}</h3>
                  {service.subtitle &&
                <span className="inline-block px-2 py-0.5 bg-[#a3e635]/15 text-[#a3e635] text-xs font-semibold rounded-full">
                      {service.subtitle}
                    </span>
                }
                </div>
              </div>
              
              <ul className="space-y-3 ml-15">
                {service.items.map((item, i) =>
              <li key={i} className="flex items-start gap-3 text-slate-200 text-base leading-relaxed">
                    <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-1" />
                    <span>{item}</span>
                  </li>
              )}
              </ul>
              
              {service.highlight &&
            <p className="mt-5 pt-4 border-t border-white/10 text-[#a3e635] text-base font-medium leading-relaxed">
                  {service.highlight}
                </p>
            }
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Additional Services Section - HERVORGEHOBEN für Mobile
const AdditionalServicesSection = () => {
  const additionalServices = [
  {
    id: "followup",
    icon: <Target strokeWidth={1.5} />,
    title: "Aktive Vorbereitung von Folgeaufträgen",
    description: "Ich erkenne während der Abnahme gezielt fehlende oder sinnvolle Erweiterungen (Wallbox, Speicher, Steuerung), technische Optimierungen und zukünftige Ausbaustufen.",
    details: [
    "Verständlich erklärt für den Kunden",
    "Sauber formuliert für die Firma",
    "Klare Grundlage für ein Angebot"],

    result: "Die Firma bekommt konkrete, vorqualifizierte Folgeaufträge – ohne eigenen Aufwand."
  },
  {
    id: "documentation",
    icon: <Camera strokeWidth={1.5} />,
    title: "Hochwertige Dokumentation für Marketing & Social Media",
    description: "Ich dokumentiere die Anlage nicht nur technisch, sondern auch visuell: strukturierte Foto- und Videoaufnahmen auch mit Drohne, Vorher-/Nachher-Eindrücke und echte Einblicke in das Projekt.",
    details: [
    "Aufbereitbares Material für Social Media",
    "Nutzbar für Website, Werbung oder Content"],

    result: "Die Firma spart Zeit, bekommt Content und wirkt professioneller nach außen."
  },
  {
    id: "satisfaction",
    icon: <Star strokeWidth={1.5} />,
    title: "Kundenzufriedenheit auf einem anderen Level",
    subtitle: "Das ist meine größte Stärke",
    description: "Ich übernehme gezielt auch Situationen, in denen der Kunde unsicher ist, Fragen offen sind oder Unzufriedenheit entstanden ist.",
    details: [
    "Aus kritischen Situationen wieder Vertrauen aufbauen",
    "Den Kunden emotional 'abholen'",
    "Das Projekt positiv abschließen"],

    resultItems: [
    "Zufriedene Kunden",
    "Weniger Beschwerden",
    "Höhere Zahlungsbereitschaft",
    "Echte Weiterempfehlungen",
    "Positive Google-Bewertungen"]

  }];


  return (
    <section className="relative py-16 md:py-24 overflow-hidden" data-testid="additional-services-section">
      {/* Auffälliger Hintergrund */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1a1a] via-[#0d2525] to-[#0a1a1a]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(34,211,238,0.15)_0%,_transparent_70%)]" />
      
      {/* Leuchtende Ränder */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#22d3ee] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#a3e635] to-transparent" />
      
      <div className="container mx-auto px-5 max-w-3xl relative z-10">
        <div className="text-center mb-14">
          {/* Auffälliges Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#a3e635]/20 to-[#22d3ee]/20 border-2 border-[#22d3ee] rounded-full mb-5 animate-pulse-slow">
            <Zap className="w-5 h-5 text-[#a3e635]" />
            <span className="text-[#22d3ee] text-sm font-bold uppercase tracking-wider">Mein echter Unterschied</span>
            <Zap className="w-5 h-5 text-[#a3e635]" />
          </div>
          
          <h2 className="text-2xl md:text-4xl font-bold mb-3">
            <span className="gradient-text">ZUSATZLEISTUNGEN</span>
          </h2>
          <p className="text-slate-300 text-base font-medium">Das ist es, was mich wirklich abhebt!</p>
        </div>
        
        <div className="space-y-8">
          {additionalServices.map((service, index) =>
          <div
            key={service.id}
            className="bg-black/40 backdrop-blur-sm border-2 border-[#22d3ee]/30 rounded-2xl p-6 md:p-8 hover:border-[#a3e635]/50 transition-colors duration-300"
            data-testid={`additional-service-${service.id}`}>

              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-[#a3e635]/20 to-[#22d3ee]/20 rounded-xl border border-[#a3e635]/30">
                  <div className="text-[#a3e635]">{service.icon}</div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold mb-1 text-white">{service.title}</h3>
                  {service.subtitle &&
                <span className="inline-block px-2 py-0.5 bg-[#a3e635]/20 text-[#a3e635] text-xs font-bold rounded-full mb-2">
                      {service.subtitle}
                    </span>
                }
                </div>
              </div>
              
              <p className="text-slate-300 text-base leading-relaxed mb-5">{service.description}</p>
              
              <ul className="space-y-3 mb-5">
                {service.details.map((detail, i) =>
              <li key={i} className="flex items-start gap-3 text-slate-200 text-base leading-relaxed">
                    <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-1" />
                    <span>{detail}</span>
                  </li>
              )}
              </ul>
              
              {service.result &&
            <div className="p-4 bg-gradient-to-r from-[#a3e635]/15 to-[#22d3ee]/15 rounded-xl border border-[#a3e635]/40">
                  <p className="text-white text-sm font-semibold flex items-start gap-2 leading-relaxed">
                    <Zap className="w-4 h-4 text-[#a3e635] flex-shrink-0 mt-0.5" />
                    {service.result}
                  </p>
                </div>
            }
              
              {service.resultItems &&
            <div className="p-4 bg-gradient-to-r from-[#a3e635]/15 to-[#22d3ee]/15 rounded-xl border border-[#a3e635]/40">
                  <ul className="grid grid-cols-2 gap-2">
                    {service.resultItems.map((item, i) =>
                <li key={i} className="text-white text-xs flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-[#a3e635] rounded-full flex-shrink-0" />
                        {item}
                      </li>
                )}
                  </ul>
                </div>
            }
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Killer Points Section - Mobile optimiert
const KillerPointsSection = () => {
  const killerPoints = [
  {
    number: "01",
    icon: <FileCheck strokeWidth={1.5} className="w-8 h-8" />,
    title: "Du bekommst den Auftrag wirklich abgeschlossen",
    description: "Kein halbfertiger Kunde, kein Chaos – fertig, sauber, unterschrieben"
  },
  {
    number: "02",
    icon: <TrendingUp strokeWidth={1.5} className="w-8 h-8" />,
    title: "Du verdienst zusätzlich Geld ohne Mehraufwand",
    description: "Durch vorbereitete Folgeaufträge, erkannte Potenziale und Upselling beim Kunden"
  },
  {
    number: "03",
    icon: <Heart strokeWidth={1.5} className="w-8 h-8" />,
    title: "Deine Kunden werden zufriedener – auch nach Problemen",
    description: "Weniger Stress, bessere Bewertungen, stärkere Kundenbindung"
  }];


  return (
    <section className="py-16 md:py-20" data-testid="killer-points-section">
      <div className="container mx-auto px-5">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Award className="w-5 h-5 text-[#a3e635]" />
            <h2 className="text-xl md:text-3xl font-bold">Mein Abschluss</h2>
          </div>
          <p className="text-slate-300 text-sm">Kundenzufriedenheit steht an erster Stelle.</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {killerPoints.map((point, index) =>
          <div
            key={index}
            className="card p-5 text-center"
            data-testid={`killer-point-${index + 1}`}>

              <div className="text-3xl md:text-4xl font-black gradient-text mb-2">{point.number}</div>
              
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-[#a3e635]/20 to-[#22d3ee]/20 text-[#a3e635] mb-3">
                {point.icon}
              </div>
              
              <h3 className="text-base md:text-lg font-bold mb-1 leading-tight">{point.title}</h3>
              <p className="text-slate-300 text-xs md:text-sm leading-relaxed">{point.description}</p>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Contact Section - Mobile optimiert
const ContactSection = () =>
<section className="contact-section py-16 md:py-24" data-testid="contact-section">
    <div className="contact-gradient" />
    
    <div className="container mx-auto px-5 relative z-10">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-4xl font-bold mb-2">
          Direkt <span className="gradient-text">Kontakt</span> aufnehmen
        </h2>
      </div>
      
      <div className="max-w-md mx-auto">
        <div className="contact-card text-center">
          {/* Portrait im Kreis - größer */}
          <div className="flex justify-center mb-5">
            <div className="w-44 h-44 rounded-full bg-black p-0.5 ring-2 ring-[#22d3ee] shadow-[0_0_20px_rgba(34,211,238,0.3)] overflow-hidden">
              <img
              src={PORTRAIT}
              alt="Enes Dedic"
              className="w-full h-full object-cover object-[center_20%] scale-110 rounded-full"
              data-testid="contact-portrait" />

            </div>
          </div>
          
          <h3 className="text-xl font-bold mb-1">Enes Dedic</h3>
          <p className="text-[#22d3ee] text-sm font-medium mb-5">
            Energietechniker | Elektromeister | PV-Systemabnehmer
          </p>
          
          <div className="space-y-2 mb-6 text-sm">
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Phone className="w-4 h-4 text-[#a3e635]" />
              <span>{CONTACT.phone}</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Mail className="w-4 h-4 text-[#a3e635]" />
              <span>{CONTACT.email}</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <Globe className="w-4 h-4 text-[#a3e635]" />
              <span>{CONTACT.web}</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-slate-300">
              <MapPin className="w-4 h-4 text-[#a3e635]" />
              <span>{CONTACT.region}</span>
            </div>
          </div>
          
          <div className="flex flex-col gap-3 print:hidden">
            <a
            href={CONTACT.phoneLink}
            className="btn-primary text-base justify-center w-full"
            data-testid="contact-call-button">

              <Phone size={18} />
              Jetzt anrufen
            </a>
            <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-base justify-center w-full"
            data-testid="contact-whatsapp-button">

              <MessageCircle size={18} />
              WhatsApp schreiben
            </a>
            <a
            href={CONTACT.emailLink}
            className="btn-secondary text-base justify-center w-full"
            data-testid="contact-email-button">

              <Mail size={18} />
              E-Mail senden
            </a>
          </div>
          <p className="text-slate-400 text-xs text-center mt-3 print:hidden">Kostenlose Erstberatung · Unverbindlich · Schnelle Antwort</p>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-12 pt-6 border-t border-white/10 text-center">
        <img
        src={LOGO_MAIN}
        alt="ENTHIDA"
        className="h-8 mx-auto mb-3 opacity-70" />

        <p className="text-slate-500 text-xs">
          © {new Date().getFullYear()} ENTHIDA – Smart Energy Systems. Alle Rechte vorbehalten.
        </p>
        <div className="flex justify-center gap-4 mt-2 print:hidden">
          <Link to="/impressum" className="text-slate-500 text-xs hover:text-[#a3e635] transition-colors" data-testid="footer-impressum">Impressum</Link>
          <span className="text-slate-600">|</span>
          <Link to="/datenschutz" className="text-slate-500 text-xs hover:text-[#a3e635] transition-colors" data-testid="footer-datenschutz">Datenschutz</Link>
        </div>
      </div>
    </div>
  </section>;


// Elektroservice Section - Für Betriebe UND Selberbauer
const PrivateServicesSection = () =>
<section className="py-16 md:py-24 relative overflow-hidden" data-testid="private-services-section">
    {/* Subtiler Hintergrund */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a1510]/50 to-transparent" />
    
    <div className="container mx-auto px-5 relative z-10">
      <div className="max-w-3xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <Settings className="w-6 h-6 text-[#a3e635]" />
            <h2 className="text-2xl md:text-3xl font-bold">
              Elektroservice <span className="gradient-text">nach Bedarf</span>
            </h2>
          </div>
          <p className="text-slate-400 text-sm">Für Betriebe, Solarfirmen und Selberbauer</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Karte 1: Für Betriebe */}
          <div className="card p-5 md:p-6 border-[#a3e635]/20 bg-gradient-to-br from-[#0f1115] to-[#0a1510]" data-testid="service-betriebe">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#a3e635]/20 to-[#22d3ee]/20 rounded-lg">
                <HeadphonesIcon className="w-5 h-5 text-[#a3e635]" />
              </div>
              <div>
                <span className="inline-block px-2 py-0.5 bg-[#a3e635]/15 text-[#a3e635] text-xs font-semibold rounded-full">
                  Für Betriebe
                </span>
              </div>
            </div>
            
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              Auftragsspitzen, Fachkräftemangel oder kurzfristige Engpässe – ich bin als 
              <span className="text-white font-medium"> zuverlässiger Fachmann sofort einsatzbereit</span>.
            </p>
            
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>AC-seitige Installation, Inbetriebnahme & Abnahme</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Kurzfristige Verstärkung bei Personalengpässen</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Projektleitung & Baustellenkoordination auf Abruf</span>
              </li>
            </ul>
          </div>

          {/* Karte 2: Für Selberbauer */}
          <div className="card p-5 md:p-6 border-[#22d3ee]/20 bg-gradient-to-br from-[#0f1115] to-[#0a1520]" data-testid="service-selberbauer">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-[#22d3ee]/20 to-[#a3e635]/20 rounded-lg">
                <Zap className="w-5 h-5 text-[#22d3ee]" />
              </div>
              <div>
                <span className="inline-block px-2 py-0.5 bg-[#22d3ee]/15 text-[#22d3ee] text-xs font-semibold rounded-full">
                  Für Selberbauer
                </span>
              </div>
            </div>
            
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              Du baust deine PV-Anlage selbst? Dann übernehme ich als Elektromeister 
              <span className="text-white font-medium"> die komplette fachgerechte Elektroinstallation</span> – alles, was in Profi-Hände gehört.
            </p>
            
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Zählerschrankumbau & fachgerechte Absicherung</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Elektroinstallation, Neuinstallation & Sanierung</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Einhaltung aktueller VDE-Normen sicherstellen</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Fehlerdiagnose, Wechselrichter-Diagnose & Software-Updates</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Wallbox-Installation & Smart-Energy-Lösungen</span>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <CheckCircle className="w-4 h-4 text-[#22d3ee] flex-shrink-0 mt-0.5" />
                <span>Sicherheitsprüfungen & E-Check</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Gemeinsamer CTA */}
        <div className="mt-6 text-center">
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto print:hidden">
            <a
            href={CONTACT.phoneLink}
            className="btn-primary text-sm justify-center flex-1"
            data-testid="private-call-button">
              <Phone size={16} />
              Jetzt anfragen
            </a>
            <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary text-sm justify-center flex-1"
            data-testid="private-whatsapp-button">
              <MessageCircle size={16} />
              WhatsApp
            </a>
          </div>
          <p className="text-slate-400 text-xs text-center mt-2 print:hidden">Kostenlose Erstberatung · Unverbindlich</p>
          
          {/* Print-only Kontaktdaten */}
          <div className="hidden print:block pt-4 text-center border-t border-white/10 mt-4">
            <p className="text-slate-300 text-sm">
              <span className="text-[#a3e635] font-semibold">Kontakt:</span> {CONTACT.phone} | {CONTACT.email}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>;


// Remove Emergent watermark on mount and continuously
const useRemoveWatermark = () => {
  useEffect(() => {
    const remove = () => {
      // Remove elements after #root
      const root = document.getElementById('root');
      if (root) {
        let sibling = root.nextElementSibling;
        while (sibling) {
          const next = sibling.nextElementSibling;
          sibling.remove();
          sibling = next;
        }
      }
      // Remove any fixed-position elements not inside #root
      document.querySelectorAll('body > div, body > a, body > iframe').forEach(el => {
        if (el.id === 'root') return;
        const style = window.getComputedStyle(el);
        if (style.position === 'fixed' || style.position === 'absolute') {
          el.remove();
        }
      });
      // Remove anything with emergent in text/href/class
      document.querySelectorAll('a, div, span, iframe').forEach(el => {
        const text = el.textContent || '';
        const href = el.getAttribute('href') || '';
        const cls = el.className || '';
        if (
          (text.includes('Emergent') || text.includes('emergent')) &&
          !el.closest('#root')
        ) {
          el.remove();
        }
        if (href.includes('emergent') && !el.closest('#root')) {
          el.remove();
        }
        if ((cls.includes && cls.includes('emergent')) && !el.closest('#root')) {
          el.remove();
        }
      });
    };

    // Run immediately
    remove();

    // Run after short delay (for late-injected elements)
    const t1 = setTimeout(remove, 500);
    const t2 = setTimeout(remove, 1500);
    const t3 = setTimeout(remove, 3000);

    // MutationObserver for dynamically injected elements
    const observer = new MutationObserver(() => remove());
    observer.observe(document.body, { childList: true, subtree: false });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      observer.disconnect();
    };
  }, []);
};

// Main App Component
function App() {
  useRemoveWatermark();

  return (
    <div className="min-h-screen bg-[#020408]" data-testid="app-container">
      <HeroSection />
      <AboutSection />
      {/* Print Seite 2 Wrapper */}
      <div className="print-page-2">
        <ServicesSection />
        <AdditionalServicesSection />
      </div>
      {/* Print Seite 3 Wrapper */}
      <div className="print-page-3">
        <KillerPointsSection />
        <PrivateServicesSection />
        <ContactSection />
      </div>
      <FixedPrintButton />
      <CookieBanner />
    </div>);

}

export default App;